import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaClipboardCheck, 
  FaDownload,
  FaBook,
  FaCheckCircle,
  FaUsers,
  FaShieldAlt,
  FaGraduationCap,
  FaHandshake
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';

const CompanyPolicy = ({ embedded = false }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [handbookFile, setHandbookFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEmployeeHandbook();
  }, []);

  const fetchEmployeeHandbook = async () => {
    try {
      setIsLoading(true);
      const templatesRef = collection(db, 'templatesCollections');
      
      // Try exact match first
      let q = query(templatesRef, where('title', '==', 'Employee Handbook'));
      let querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Get the most recent version
        const docs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by version number (assuming version is in format like "1.0", "2.1", etc.)
        const sortedDocs = docs.sort((a, b) => {
          const versionA = parseFloat(a.version) || 0;
          const versionB = parseFloat(b.version) || 0;
          return versionB - versionA;
        });
        
        if (sortedDocs.length > 0) {
          const handbookDoc = sortedDocs[0];
          setHandbookFile(handbookDoc);
        }
      } else {
        // Let's check if there are any documents with similar titles
        const allDocsSnapshot = await getDocs(collection(db, 'templatesCollections'));
        const allDocs = allDocsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const similarTitles = allDocs.filter(doc => 
          doc.title && (doc.title.toLowerCase().includes('handbook') || 
          doc.title.toLowerCase().includes('employee'))
        );
        
        // If we find a similar title, use the first one
        if (similarTitles.length > 0) {
          const similarDoc = similarTitles[0];
          setHandbookFile(similarDoc);
        }
      }
    } catch (error) {
      console.error('Error fetching Employee Handbook:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/portal/hr/materials');
  };

  const handleDownload = async () => {
    if (!handbookFile) {
      alert('Employee Handbook template not found. Please contact support.');
      return;
    }

    try {
      // If the file has a direct URL, download it directly
      if (handbookFile.fileUrl) {
        // Create a temporary link element to trigger download
        const link = document.createElement('a');
        link.href = handbookFile.fileUrl;
        link.download = handbookFile.fileName || `Employee_Handbook_Template_v${handbookFile.version}.${getFileExtension(handbookFile.fileName)}`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // If no direct URL, show message to contact support
        alert('Employee Handbook template is not available for download. Please contact support.');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  const getFileExtension = (fileName) => {
    if (!fileName) return 'txt';
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : 'txt';
  };

  const keyPoints = [
    {
      icon: <FaUsers className="h-6 w-6 text-blue-500" />,
      title: "Company Culture & Values",
      description: "Template sections for defining your core values, mission, and company culture"
    },
    {
      icon: <FaShieldAlt className="h-6 w-6 text-green-500" />,
      title: "Employment Policies",
      description: "Comprehensive policy templates for equal opportunity, workplace safety, and compliance"
    },
    {
      icon: <FaGraduationCap className="h-6 w-6 text-purple-500" />,
      title: "Professional Development",
      description: "Guidelines for training programs, career growth, and employee development"
    },
    {
      icon: <FaHandshake className="h-6 w-6 text-orange-500" />,
      title: "Code of Conduct",
      description: "Professional behavior standards and ethical guidelines for your team"
    }
  ];

  return (
    <div className={`${embedded ? '' : 'relative min-h-screen p-6'} text-gray-900 dark:text-white`}>
      {!embedded && <PortalBackground />}
      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        {!embedded && (
          <div className="flex items-center justify-between mb-8 relative">
            <div className="flex-1 flex items-center">
              <BackButton onClick={handleBack} />
            </div>
            <div className="flex items-center absolute left-1/2 transform -translate-x-1/2">
              <h1 className={`text-3xl font-bold tracking-tight text-gray-900 dark:text-white`}>Employee Handbook Template</h1>
            </div>
            <div className="flex-1"></div>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Full Width - Template Sections */}
          <div className="lg:col-span-5 space-y-8">
            {/* Handbook Overview */}
            <div className={`p-8 rounded-lg shadow-lg border ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>

              
              <div className="mb-6">
                <h3 className={`text-xl font-semibold mb-3 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  About This Template
                </h3>
                <p className={`text-lg leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  This comprehensive Employee Handbook template provides you with a professional foundation for your company's 
                  policies and procedures. It's designed to help you establish clear expectations, maintain compliance, and 
                  create a positive workplace culture. Customize each section to align with your business values and specific needs.
                </p>
                

              </div>

              <div className="flex justify-center">
                {isLoading ? (
                  <div className="flex items-center px-8 py-4 bg-gray-400 text-white font-semibold rounded-lg text-lg">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                    Loading Template...
                  </div>
                ) : handbookFile && handbookFile.fileUrl ? (
                  <button
                    onClick={handleDownload}
                    className={`flex items-center px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg text-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1`}
                  >
                    <FaDownload className="mr-3 h-6 w-6" />
                    Download Handbook Template
                  </button>
                ) : handbookFile ? (
                  <div className="flex flex-col items-center px-8 py-4 bg-yellow-500 text-white font-semibold rounded-lg text-lg">
                    <FaBook className="mr-3 h-6 w-6 mb-2" />
                    <span>Template Not Available</span>
                    <p className="text-sm font-normal mt-2 text-center">
                      Please contact support to access the Employee Handbook template.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center px-8 py-4 bg-gray-400 text-white font-semibold rounded-lg text-lg">
                    <FaBook className="mr-3 h-6 w-6 mb-2" />
                    <span>Template Not Available</span>
                    <p className="text-sm font-normal mt-2 text-center">
                      Please contact support to upload the Employee Handbook template.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Template Sections */}
            <div className={`p-8 rounded-lg shadow-lg border ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Template Sections
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {keyPoints.map((point, index) => (
                  <div key={index} className={`p-4 rounded-lg border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="flex-shrink-0">
                        {point.icon}
                      </div>
                      <div>
                        <h4 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {point.title}
                        </h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {point.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Implementation Guide */}
            <div className={`p-8 rounded-lg shadow-lg border ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <h3 className={`text-xl font-bold mb-6 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Implementation Guide
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FaCheckCircle className={`h-5 w-5 text-green-500 mt-0.5 flex-shrink-0`} />
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    This template is designed to be customized for your specific business needs and industry requirements.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheckCircle className={`h-5 w-5 text-green-500 mt-0.5 flex-shrink-0`} />
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Review and modify all policies to ensure they align with your company culture and legal obligations.
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FaCheckCircle className={`h-5 w-5 text-green-500 mt-0.5 flex-shrink-0`} />
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Consider having your legal counsel review the final handbook before distributing to employees.
                  </p>
                </div>
                {!handbookFile && !isLoading && (
                  <div className="flex items-start space-x-3">
                    <FaCheckCircle className={`h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0`} />
                    <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      The Employee Handbook template will be available for download once it's uploaded by our team.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPolicy; 